import { AvatarShape } from "../../../../types";
import {
  UserIcon,
  UserCircleIcon,
  UserSquareIcon,
} from "@phosphor-icons/react";
import { PaletteResponse } from "pages/media/types";
import { loadImageRatio } from "../../../../helpers";
import { getAvatarImagePath } from "../../../../helpers/itemsHelper";
import type { GradientDefinition } from "types";
import { CustomSegmented } from "../../../controls/CustomSegmented";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { getUpdatedAvatarPositionOnShapeChange } from "../../../../helpers/tabHelpers";

export const AvatarShapeFeature = ({
  brandKitPalette,
  previewWidth,
  previewHeight,
}: {
  brandKitPalette: PaletteResponse;
  previewWidth: number;
  previewHeight: number;
}) => {
  const { presenter, setPresenter } = useTemplateEditorStore((store) => ({
    presenter: store.templateState.presenter,
    setPresenter: store.setPresenter,
  }));

  const addTemplateUpdate = useTemplatesStatusStore(
    (state) => state.addTemplateUpdate
  );

  const avatarShapeOptions = [
    {
      value: AvatarShape.Regular,
      icon: <UserIcon size={24} />,
    },
    {
      value: AvatarShape.Circle,
      icon: <UserCircleIcon size={24} />,
    },
    {
      value: AvatarShape.Box,
      icon: <UserSquareIcon size={24} />,
    },
  ];

  // if avatar is not square, we need to update it's position when changing shape
  // to keep it centered in the same place
  const handleAvatarShapeChange = async (avatarShape: AvatarShape) => {
    const height = presenter.size.height;
    let width = (height * previewHeight) / previewWidth;

    const isRegular = avatarShape === AvatarShape.Regular;

    let avatarShapeColor: string | GradientDefinition | undefined =
      presenter.background ?? brandKitPalette.primary_color;

    const avatarPath = getAvatarImagePath(presenter);
    if (!avatarPath) return;

    if (isRegular) {
      avatarShapeColor = undefined;
      const ratio = await loadImageRatio(avatarPath);
      width = (height * previewHeight * ratio) / previewWidth;
    }

    const oldwidthInPixels = presenter.size.width * previewWidth;
    const newwidthInPixels = width * previewWidth;
    const heightInPixels = height * previewHeight;
    const avatarPositionXShouldBeUpdated =
      oldwidthInPixels !== heightInPixels ||
      newwidthInPixels !== heightInPixels;

    setPresenter({
      avatarShape: avatarShape,
      background: avatarShapeColor,
      position: avatarPositionXShouldBeUpdated
        ? {
            x: getUpdatedAvatarPositionOnShapeChange(
              width,
              presenter.position.x,
              presenter.size.width
            ),
            y: presenter.position.y,
          }
        : presenter.position,
      size: {
        width,
        height,
      },
      stroke: isRegular ? undefined : presenter.stroke,
    });

    addTemplateUpdate();
  };

  return (
    <CustomSegmented
      value={presenter.avatarShape}
      customOptions={avatarShapeOptions}
      onChange={(value: AvatarShape) => {
        handleAvatarShapeChange(value).catch((e) => console.error(e));
      }}
    />
  );
};
